import { useState, useEffect, useMemo } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { X, Printer, CheckSquare, Square } from "lucide-react";
import api from "@/lib/api";
import { MODULES, MODULE_ORDER } from "@/lib/modules";

export default function QRLabelsPanel({ onClose }) {
  const [moduleKey, setModuleKey] = useState("closures");
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState({});
  const [cols, setCols] = useState(3);
  const cfg = MODULES[moduleKey];
  const origin = window.location.origin;

  useEffect(() => {
    api.get(`/${moduleKey}`).then((r) => {
      setItems(r.data);
      setSelected(Object.fromEntries(r.data.map((it) => [it.id, true])));
    });
  }, [moduleKey]);

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const allOn = items.length > 0 && items.every((it) => selected[it.id]);
  const toggleAll = () => setSelected(Object.fromEntries(items.map((it) => [it.id, !allOn])));

  const chosen = useMemo(() => items.filter((it) => selected[it.id]), [items, selected]);

  return (
    <div className="absolute inset-0 bg-slate-900 flex flex-col" data-testid="qr-labels-panel">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700 no-print">
        <h2 className="font-heading font-bold text-lg">Etykiety QR do druku</h2>
        <button onClick={onClose} data-testid="qrlabels-close" className="text-slate-400 hover:text-white"><X size={20} /></button>
      </div>

      <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-800 no-print flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-400">Moduł:</span>
          <select value={moduleKey} onChange={(e) => setModuleKey(e.target.value)} data-testid="qrlabels-module"
            className="bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm">
            {MODULE_ORDER.map((k) => <option key={k} value={k}>{MODULES[k].label}</option>)}
          </select>
        </div>
        <button onClick={toggleAll} data-testid="qrlabels-selectall" className="flex items-center gap-1.5 text-sm text-slate-300 hover:text-cyan-400">
          {allOn ? <CheckSquare size={16} /> : <Square size={16} />} Zaznacz wszystkie
        </button>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-400">Kolumny:</span>
          <select value={cols} onChange={(e) => setCols(Number(e.target.value))}
            className="bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm">
            {[2, 3, 4, 5].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <span className="text-sm text-slate-500">Wybrano: <b className="text-slate-300">{chosen.length}</b></span>
        <button onClick={() => window.print()} data-testid="qrlabels-print"
          className="ml-auto bg-blue-600 hover:bg-blue-500 rounded px-4 py-1.5 text-sm flex items-center gap-2">
          <Printer size={15} /> Drukuj etykiety
        </button>
      </div>

      {/* Selection strip */}
      <div className="px-5 py-2 border-b border-slate-800 no-print flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
        {items.map((it) => (
          <button key={it.id} onClick={() => toggle(it.id)}
            className={`text-xs px-2 py-1 rounded border ${selected[it.id] ? "bg-cyan-700/40 border-cyan-600 text-cyan-200" : "bg-slate-800 border-slate-700 text-slate-400"}`}>
            {it[cfg.display] || it.name || it.id.slice(0, 6)}
          </button>
        ))}
        {items.length === 0 && <span className="text-sm text-slate-500">Brak obiektów w tym module</span>}
      </div>

      {/* Printable area */}
      <div className="flex-1 overflow-y-auto p-5 print-area">
        <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
          {chosen.map((it) => (
            <div key={it.id} className="qr-label bg-white text-slate-900 rounded border border-slate-300 p-3 flex items-center gap-3" data-testid={`qr-label-${it.id}`}>
              <QRCodeCanvas value={`${origin}/o/${moduleKey}/${it.id}`} size={78} level="M" />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">{cfg.singular}</div>
                <div className="font-bold text-sm truncate">{it[cfg.display] || it.name}</div>
                {cfg.fields.slice(1, 3).map((f) => it[f.name] ? (
                  <div key={f.name} className="text-[11px] text-slate-600 truncate">{it[f.name]}</div>
                ) : null)}
                <div className="text-[9px] text-slate-400 mt-1">FiberPass</div>
              </div>
            </div>
          ))}
        </div>
        {chosen.length === 0 && <div className="text-center text-slate-500 py-10 no-print">Zaznacz obiekty, aby wygenerować etykiety</div>}
      </div>
    </div>
  );
}
